import { useCalculations } from '@commenda/api';
import { TaxCalculationData } from '@commenda/api/new-sales-tax/calculations/types';
import { LoadingIndicator } from '@commenda/kakashi';
import { useCallback, useEffect, useState } from 'react';
import { SALES_TAX_TABLE_IDS } from '../common/constants';
import { calculateDateRange } from '../common/filters/DateFilter/utils';
import { CalculationFilterState } from '../common/filters/types';
import { calculatePaginationRangeForCursorBasedPagination } from '../common/usePaginationRange';
import {
  loadFiltersFromSessionStorage,
  saveFiltersToSessionStorage,
} from '../common/utils/sessionStorage';
import { formatNumberWithCommas } from '../utils';
import CalculationTabHeader from './CalculationTabHeader';
import CalculationTable from './CalculationTable';

const CalculationDashboard = ({ corporationId }: { corporationId: number }) => {
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const PAGE_SIZE = 50;
  const [calculationsList, setCalculationsList] = useState<
    TaxCalculationData[]
  >([]);
  const [filters, setInternalFilters] = useState<CalculationFilterState>({
    corporationId,
  });
  const [searchTerm, setSearchTerm] = useState<string>('');

  const setFilters = useCallback(
    (
      updater:
        | CalculationFilterState
        | ((prev: CalculationFilterState) => CalculationFilterState)
    ) => {
      setInternalFilters((prev) => {
        const next =
          typeof updater === 'function'
            ? (
                updater as (p: CalculationFilterState) => CalculationFilterState
              )(prev)
            : updater;
        saveFiltersToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.CALCULATIONS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const handleSearch = useCallback((term: string) => {
    setCurrentCursor(null);
    setCursorHistory([]);
    setSearchTerm(term);
  }, []);

  // API integration
  const {
    calculations: calculationsData,
    totalCalculations,
    cursor: nextCursor,
    error: calculationsError,
    isLoading: isLoadingCalculations,
  } = useCalculations({
    filters: { ...filters, search_term: searchTerm || undefined },
    cursor: currentCursor,
    limit: PAGE_SIZE,
  });

  const loadCalculationFiltersFromSessionStorage = useCallback(() => {
    const savedFilters: CalculationFilterState = loadFiltersFromSessionStorage(
      corporationId,
      SALES_TAX_TABLE_IDS.CALCULATIONS
    );
    return {
      corporationId,
      ...savedFilters,
    };
  }, [corporationId]);

  useEffect(() => {
    setFilters(loadCalculationFiltersFromSessionStorage());
  }, [loadCalculationFiltersFromSessionStorage, setFilters]);

  useEffect(() => {
    if (!isLoadingCalculations) {
      if (
        JSON.stringify(calculationsData) !== JSON.stringify(calculationsList)
      ) {
        setCalculationsList(calculationsData);
      }
    }
  }, [calculationsData, isLoadingCalculations, calculationsList]);

  const handlePageChange = (newCursor: string | null) => {
    if (newCursor === currentCursor) {
      // Going back
      const previousCursor = cursorHistory[cursorHistory.length - 2];
      setCursorHistory((prev) => prev.slice(0, -1));
      setCurrentCursor(previousCursor || null);
    } else {
      // Going forward
      setCursorHistory((prev) => [...prev, currentCursor]);
      setCurrentCursor(newCursor);
    }
  };

  const { startIndexOfPage, endIndexOfPage, totalItemsForPage } =
    calculatePaginationRangeForCursorBasedPagination({
      cursorHistoryLength: cursorHistory.length,
      pageSize: PAGE_SIZE,
      currentItemsCount: calculationsList.length,
      totalItems: totalCalculations || 0,
    });

  // Build DataTable filterConfig: expose a DATE_RANGE for CalculationDate
  const dataTableFilterConfig = {
    filters: [
      {
        columnId: 'CalculationDate',
        type: 'DATE_RANGE',
        icon: null,
        label: 'Period',
      },
    ],
    activeFilters: (() => {
      if (filters.from_date && filters.to_date) {
        return {
          CalculationDate: `custom:${filters.from_date},${filters.to_date}`,
        };
      }
      return {} as Record<string, string | string[]>;
    })(),
    onFilterChange: (active: Record<string, string | string[]>) => {
      setCurrentCursor(null);
      const val = active?.CalculationDate as string | undefined;
      if (!val) {
        setFilters((prev) => {
          const next = { ...prev };
          delete next.from_date;
          delete next.to_date;
          return next;
        });
        return;
      }
      if (val.startsWith('custom:')) {
        const range = val.replace('custom:', '');
        const [from, to] = range.split(',');
        setFilters((prev) => ({ ...prev, from_date: from, to_date: to }));
        return;
      }
      if (val.startsWith('preset:')) {
        const code = val.replace('preset:', '');
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(code);
        setFilters((prev) => ({ ...prev, from_date: fromDate, to_date: toDate }));
        return;
      }
      if (/^\d+$/.test(val)) {
        const { from_date: fromDate, to_date: toDate } = calculateDateRange(val);
        setFilters((prev) => ({ ...prev, from_date: fromDate, to_date: toDate }));
      }
    },
  };

  if (!calculationsData || calculationsError) {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <LoadingIndicator />
      </main>
    );
  }

  return (
    <main className="flex flex-col gap-6 mt-3xl">
      <CalculationTabHeader calculationCount={totalCalculations || 0} />
      <div>
        {totalItemsForPage > 0 ? (
          <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
            Showing {startIndexOfPage}-{endIndexOfPage} of{' '}
            {formatNumberWithCommas(totalItemsForPage)}{' '}
            {totalItemsForPage === 1 ? 'calculation' : 'calculations'}
          </div>
        ) : (
          <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
            Showing 0-0 of 0 calculations
          </div>
        )}
        <CalculationTable
          data={calculationsList}
          pageSize={PAGE_SIZE}
          nextCursor={nextCursor}
          currentCursor={currentCursor}
          onPageChange={handlePageChange}
          corporationId={corporationId}
          filterConfig={dataTableFilterConfig}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
        />
      </div>
    </main>
  );
};

export default CalculationDashboard;
